// Count-based pluralization for user-facing copy.
//
// Returns `singular` when count is exactly 1, otherwise `plural`. When `plural`
// is omitted it defaults to `${singular}s` — sufficient for regular English
// nouns. Pass an explicit `plural` for irregular forms or when the variation
// includes a verb conjugation (e.g. 'recipient has' / 'recipients have').
//
// Distinct from `pluralTerm` in `term.helper.tsx` — that one builds the
// *possessive* of a sport-terminology noun ("Athletes'" vs "Athletes"). This
// one selects the singular/plural form by count for body copy.
export const pluralize = (count: number, singular: string, plural?: string): string =>
  count === 1 ? singular : (plural ?? `${singular}s`);

// Numeric-aware, case-insensitive string compare for display sorting — 'U9' sorts before
// 'U11', where a plain lexical compare would put it last.
export const compareNatural = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

// Indefinite article for a word — 'an' before a leading vowel letter
// ('an Ice Time', 'a Session'); a vowel-SOUND check is deliberately out of scope.
export const indefiniteArticle = (word: string): string => (/^[aeiou]/i.test(word) ? 'an' : 'a');

// Joins items with ', '. When count exceeds cap, the tail is collapsed to "and N more".
export const summarizeList = (items: string[], cap: number): string => {
  if (items.length <= cap) return items.join(', ');
  const remaining = items.length - cap;
  return `${items.slice(0, cap).join(', ')} and ${remaining} more`;
};
